import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  TextField as MuiTextField,
  Grid,
  CardHeader,
  Typography,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../redux/slices/snackbar";

import SaveCancel from "../../components/SaveCancel";
import useCafForm from "../../hooks/useCafForm";
import CafFields from "./components/CafFields";
import AddressDetail from "../../components/AddressDetail";
import { a11yProps } from "../../utils/common";
import TabPanel from "../../components/TabPanel";
import moment from "moment";
import KycDocuments from "./components/KycDocuments";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const useTabStyles = styled({
  root: {
    justifyContent: "center",
    width: "1000px",
  },
  scroller: {
    flexGrow: "0",
  },
});

const classes = useTabStyles();

const validationSchema = Yup.object().shape({
  fullName: Yup.string().required("Required"),
  userName: Yup.string()
    .required("Required")
    .matches(/^[a-zA-Z0-9,@#*._-]+$/, "Please enter valid username"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  emailId: Yup.string("Enter email id")
    .required("Email id is required")
    .email("Enter a valid email"),
  plan: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required("Plan is required"),
  connectionType: Yup.string().required("Required"),
  addressProofType: Yup.string().required("Required"),
  addressProofNo: Yup.string().required("Required"),
  identityProofType: Yup.string().required("Required"),
  identityProofNo: Yup.string().required("Required"),
  ipAddress: Yup.string().when("ipRequired", {
    is: true, // ✅ if ipRequired is checked
    then: (schema) => schema.required("IP Address is required"),
    otherwise: (schema) => schema.notRequired(), // ❌ if not checked
  }),
  routerNo: Yup.string().required("Required"),
  cafNumber: Yup.string().required("Required"),
  customerMileType: Yup.string().required("Required"),
  doorNo: Yup.string().required("Required"),
  city: Yup.string().required("Required"),
  state: Yup.string().required("Required"),
  country: Yup.string().required("Required"),
  pinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  insDoorNo: Yup.string().required("Required"),
  insCity: Yup.string().required("Required"),
  insState: Yup.string().required("Required"),
  insCountry: Yup.string().required("Required"),
  activationDate: Yup.string().required("Required"),
  insPinCode: Yup.object().shape({
    label: Yup.string().required(),
    value: Yup.string().required(),
  }),
  idProof: Yup.mixed()
    .required("A file is required")
    .test("fileFormat", "Only support .png, .jpeg, .jpg or .pdf", (value) => {
      return (
        value &&
        [
          "image/png", // .png
          "image/jpeg", // .jpg / .jpeg
          "application/pdf", // .pdf
        ].includes(value.type)
      );
    }),

  addressProof: Yup.mixed()
    .required("A file is required")
    .test("fileFormat", "Only support .png, .jpeg, .jpg or .pdf", (value) => {
      return (
        value &&
        [
          "image/png", // .png
          "image/jpeg", // .jpg / .jpeg
          "application/pdf", // .pdf
        ].includes(value.type)
      );
    }),
});

function EditCafForm() {
  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  const { getCafForm, cafForm, updateCafForm, isSubmitting } = useCafForm();

  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  React.useEffect(() => {
    if (Object.keys(cafForm).length !== 0) {
      const initialValues = {
        cafId: cafForm.cafId,
        mobileNo: cafForm.mobileNo,
        emailId: cafForm.emailId,
        fullName: cafForm.fullName,
        userName: cafForm.userName,
        connectionType: cafForm.connectionType,
        doorNo: cafForm.doorNo,
        area: cafForm.area,
        city: cafForm.city,
        state: cafForm.state,
        country: cafForm.country,
        pinCode: {
          value: cafForm.pinCode,
          label: cafForm.pinCode,
        },
        insDoorNo: cafForm.insDoorNo,
        insArea: cafForm.insArea,
        insCity: cafForm.insCity,
        insState: cafForm.insState,
        insCountry: cafForm.insCountry,
        insPinCode: {
          value: cafForm.insPinCode,
          label: cafForm.insPinCode,
        },
        plan: {
          value: cafForm.planId,
          label: cafForm.planName,
        },
        tenant: cafForm.tenant
          ? {
              value: cafForm.tenant.tenantId,
              label: cafForm.tenant.tenantName,
            }
          : null,
        sameAsPresentAddress: cafForm.doorNo ? 1 : 0,
        cafNumber: cafForm.cafNumber,
        addressProofType: cafForm.addressProofType,
        addressProofNo: cafForm.addressProofNo,
        identityProofType: cafForm.identityProofType,
        identityProofNo: cafForm.identityProofNo,
        ipRequired: cafForm.ipAddress ? true : false,
        ipAddress: cafForm.ipAddress,
        customerMileType: cafForm.customerMileType,
        activationDate: moment(cafForm.activationDate).format("DD-MM-YYYY"),
        routerNo: cafForm.routerNo,
      };
      reset(initialValues);
    }
  }, [cafForm]);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [errorSubmit, setErrorSubmit] = useState({
    submit: "",
  });

  const [currentCafForm, setCurrentCafForm] = useState(null);
  const { state } = useLocation();
  console.log(state, "location");
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = methods;

  const presentAddressFields = watch(["doorNo", "pinCode", "area", "city", "state", "country"]);

  const onSubmit = async (data) => {
    try {
      let formData = new FormData();

      // ✅ flatten values before sending
      data.plan = {
        planId: data.plan?.value,
        planName: data.plan?.label,
      };

      data.tenant = {
        tenantId: data.tenant?.value,
        tenantName: data.tenant?.label,
      };

      data.pinCode = data.pinCode?.label || "";
      data.insPinCode = data.insPinCode?.label || "";

      // ✅ ensure date is yyyy-MM-dd
      data.activationDate = data.activationDateStr;

      // wrap JSON
      formData.append("cafPostDto", new Blob([JSON.stringify(data)], { type: "application/json" }));
      if (data.idProof) formData.append("idProof", data.idProof);
      if (data.addressProof) formData.append("addressProof", data.addressProof);

      await updateCafForm(data.cafId, formData);
      dispatch(showSuccessSnackbar("Caf updated successfully!"));
      reset();
    } catch (err) {
      console.error(err);
      dispatch(showErrorSnackbar("Failed to update Caf"));
      reset();
    }
  };

  const cancelBtn = () => {
    if (Object.keys(state).length != 0) {
      if (state.backTo == undefined) {
        navigate("/caf/list");
      } else {
        navigate(state?.backTo);
      }
    } else {
      navigate("/caf/list");
    }
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Caf Form</Typography>
        <Typography>Edit</Typography>
      </Breadcrumbs>
    );
  };

  const fetchData = async () => {
    await getCafForm(state.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("edit_cafForm")} subheader={breadCrump()} />
          <CardContent>
            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                  <Tabs
                    classes={{ scroller: classes.scroller }}
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    aria-label="CafForm"
                    sx={{
                      justifyContent: "center",
                      width: {
                        xs: "100%", // Full width on mobile devices
                        sm: "500px", // Fixed width on small devices
                        md: "700px", // Wider on medium devices
                        lg: "900px", // Even wider on larger devices
                      },
                      minWidth: "300px", // Ensure a minimum width across all devices
                      maxWidth: "100%", // Ensure it doesn't overflow the container
                    }}
                  >
                    {" "}
                    <Tab label="Customer Details" {...a11yProps(0)} />
                    <Tab label="KYC Documents " {...a11yProps(1)} />
                    <Tab label="Address " {...a11yProps(2)} />
                  </Tabs>
                  <TabPanel value={tabValue} index={0}>
                    <CafFields
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                      isEdit={true}
                    />
                  </TabPanel>
                  <TabPanel value={tabValue} index={1}>
                    <KycDocuments validationFields={validationSchema.fields} />
                  </TabPanel>
                  <TabPanel value={tabValue} index={2}>
                    <AddressDetail
                      presentAddressFields={presentAddressFields}
                      validationFields={validationSchema.fields}
                      oldAddress={null}
                    />
                  </TabPanel>
                </Box>

                <br></br>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default EditCafForm;
